import { array, bool, func, number, object, oneOfType, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import AiOutlineMinus from '../icons/AiOutlineMinus';
import AiOutlinePlus from '../icons/AiOutlinePlus';
import { useNavigate, useParams } from 'react-router-dom';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';

const SHOW_ALL_LIMIT = 8;

export default function SearchPageSidebarTagGroup({ isMobile, searchQuery, setSearchQuery, tagPrefix, tagList, productCount }) {
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const collectionBasePath = collectionPath[0].pathBeforeParams;
    const navigate = useNavigate();
    const [selectedTag, setSelectedTag] = useState('');
    const [showAllTags, setShowAllTags] = useState(false);
    const { handle, tagFilter } = useParams();
    const searchTag = useMemo(() => searchQuery?.t ?? [], [searchQuery]);
    const tagFilterPrefix = useMemo(
        () => tagFilter
            ?.slice(0, tagFilter.indexOf('_'))
            .split('-')
            .map((p) => p.split(' '))
            .reduce((prev, curr) => prev.concat(curr), [])
            .filter((p) => p.trim() !== '') // filter out empty strings
            .map((p) => p.at(0).toUpperCase() + p.substring(1).toLowerCase())
            .join(' ')
    );
    const tagFilterValue = useMemo(() => tagFilter?.slice(tagFilter.indexOf('_') + 1).trim());
    useEffect(() => {
        if (tagFilter && tagFilterPrefix === tagPrefix) {
            setSelectedTag(tagFilterValue);
            return;
        }
        const tagPrefixStr = tagPrefix + ',';
        if (typeof searchTag === 'string') {
            if (searchTag.startsWith(tagPrefixStr)) {
                setSelectedTag(searchTag.slice(tagPrefixStr.length));
            } else {
                setSelectedTag('');
            }
            return;
        }
        setSelectedTag(searchTag.filter((tItem) => tItem.startsWith(tagPrefixStr))[0]?.slice(tagPrefixStr.length) ?? '');
    }, [searchTag]);
    const handleTagChange = (newTagValue) => {
        if (tagFilter && tagPrefix === tagFilterPrefix) {
            setSelectedTag(newTagValue);
            let searchParams = '';
            if (typeof searchQuery === 'object' && Object.keys(searchQuery).length > 0) {
                searchParams = new URLSearchParams(searchQuery);
            }
            if (newTagValue === '') {
                // Navigate back to the collection page if tagFilter is reset.
                navigate(`${collectionBasePath}/${handle}${searchQuery ? '?' + searchParams : ''}`);
            } else {
                // Otherwise, navigate to a collection page with a new tagFilter based on the selected value.
                navigate(`${collectionBasePath}/${handle}/${tagPrefix.replace(' ', '-')}_${newTagValue}${searchQuery ? '?' + searchParams : ''}`);
            }
            return;
        }
        const tagPrefixStr = tagPrefix + ',';
        const newTagList = [];
        if (Array.isArray(searchTag)) {
            newTagList.push(...searchTag.filter((tItem) => !tItem.startsWith(tagPrefixStr)));
        } else {
            if (!searchTag.startsWith(tagPrefixStr)) {
                newTagList.push(searchTag);
            }
        }
        if (newTagValue !== '') {
            newTagList.push([tagPrefix, newTagValue]);
        }
        setSelectedTag(newTagValue);
        setSearchQuery({
            ...searchQuery,
            t: newTagList,
        });
    };
    return (<>
        <div
            className={'search-sidebar--input-group'}>
            <input
                id={tagPrefix.toLowerCase() + '-none'}
                className={'search-sidebar--radio'}
                name={tagPrefix.toLowerCase()}
                type={'radio'}
                checked={selectedTag === ''}
                value={''}
                onChange={() => handleTagChange('')} />
            <label
                htmlFor={tagPrefix.toLowerCase() + '-none'}>
                <span
                    className={'input-value'}>None</span>
            </label>
        </div>
        {tagList.length > 0
            && tagList
                .filter((tag) => {
                    if (productCount > 0)
                        return true;
                    return selectedTag !== '' && tag.tagValue === selectedTag;
                })
                .slice(0, showAllTags === true ? undefined : SHOW_ALL_LIMIT)
                .map((tag, idx) => (
                    <div
                        key={idx}
                        className={'search-sidebar--input-group'}>
                        <input
                            id={tagPrefix.toLowerCase() + '-' + idx}
                            className={'search-sidebar--radio'}
                            name={tagPrefix.toLowerCase()}
                            type={'radio'}
                            checked={selectedTag === tag.tagValue}
                            value={tag.tagValue}
                            onChange={(e) => handleTagChange(e.target.value)} />
                        <label
                            htmlFor={tagPrefix.toLowerCase() + '-' + idx}>
                            <span
                                className={'input-value'}>{tag.tagValue}</span>
                            <span
                                className={'input-count'}>({productCount > 0 ? tag.tagCount : 0})</span>
                        </label>
                    </div>
                ))}
        {(tagList.length > SHOW_ALL_LIMIT && productCount > 0) && (<span
            className={'show-all-btn'}
            onClick={() => setShowAllTags(!showAllTags)}>
            {showAllTags === false && (<AiOutlinePlus
                size={'12px'}
                className={'me-1'} />)}
            {showAllTags === true && (<AiOutlineMinus
                size={'12px'}
                className={'me-1'} />)}
            Show {showAllTags ? 'less' : 'more'}
        </span>)}
    </>);
}

SearchPageSidebarTagGroup.propTypes = {
    isMobile: bool,
    searchQuery: object,
    setSearchQuery: func,
    tagPrefix: string,
    tagList: array,
    productCount: oneOfType([number, string]),
};